"use client";

import { Activity, Camera, Home } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { cn } from "@/lib/utils";

type NavIconKey = "home" | "services" | "centerPhotos";

interface NavItem {
  name: string;
  url: string;
  icon: NavIconKey;
}

interface NavBarProps {
  items: NavItem[];
  activeName?: string;
  ariaLabel?: string;
  className?: string;
  tone?: "light" | "dark";
}

const navIcons = {
  home: Home,
  services: Activity,
  centerPhotos: Camera,
} satisfies Record<NavIconKey, typeof Home>;

export function NavBar({
  items,
  activeName,
  ariaLabel = "Primary navigation",
  className,
  tone = "light",
}: NavBarProps) {
  const [activeTab, setActiveTab] = useState(items[0]?.name ?? "");
  const selectedTab = activeName ?? activeTab;
  const isDark = tone === "dark";

  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        "fixed bottom-0 sm:top-0 left-1/2 -translate-x-1/2 z-50 mb-6 sm:pt-6",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-1 rounded-full border p-1 backdrop-blur-xl transition-[background-color,border-color,box-shadow] duration-300",
          isDark
            ? "border-[#fff7d6]/18 bg-[#050505]/20 shadow-none"
            : "border-[#050505]/10 bg-[#fffdf2]/82 shadow-[0_10px_28px_rgba(80,54,0,0.08)]",
        )}
      >
        {items.map((item) => {
          const Icon = navIcons[item.icon];
          const isActive = selectedTab === item.name;

          return (
            <Link
              key={item.name}
              href={item.url}
              onClick={() => setActiveTab(item.name)}
              aria-label={item.name}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "relative cursor-pointer rounded-full px-3 py-1.5 text-[0.82rem] font-extrabold transition-[background-color,color,box-shadow,transform] duration-200 active:translate-y-px min-[430px]:px-4",
                isDark
                  ? "text-[#fff7d6]/82 hover:bg-[#fff7d6]/10 hover:text-[#fff7d6]"
                  : "text-[#4d3a00]/82 hover:bg-[#050505]/6 hover:text-[#050505]",
                isActive &&
                  (isDark
                    ? "bg-[#fff7d6] text-[#050505] shadow-[0_4px_14px_rgba(0,0,0,0.18)]"
                    : "bg-[#050505] text-[#fff7d6] shadow-[0_5px_16px_rgba(80,54,0,0.16)]"),
              )}
            >
              <span className="hidden md:inline">{item.name}</span>
              <span className="md:hidden">
                <Icon aria-hidden="true" size={17} strokeWidth={2.5} />
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
